import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveUserProfile, getUserProfile } from '../services/userService';
import { compressImage } from '../utils/imageCompression';
import { analyzeBusinessCardWithGPT } from '../services/gptVisionService';
import CameraCapture from './CameraCapture';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';

function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState('upload'); // 'upload', 'manual', 'review'
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState({
    name: '',
    position: '',
    company: '',
    email: user?.email || '',
    phone: '',
    website: '',
    bio: '',
  });
  const [extractedData, setExtractedData] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);

  const handleImageCapture = async (frontFile, backFile = null) => {
    if (!frontFile) return;

    // Store the captured image for display during processing
    setCapturedImage(frontFile);
    setIsProcessing(true);
    setError(null);

    try {
      // Compress image(s) for GPT-4o analysis
      const compressedFrontFile = await compressImage(frontFile);
      const compressedBackFile = backFile ? await compressImage(backFile) : null;
      
      // Analyze with GPT-4o Vision
      const analysis = await analyzeBusinessCardWithGPT(compressedFrontFile, 'Your Event', compressedBackFile);
      
      // Generate first-person bio from extracted data
      const generateFirstPersonBio = (data) => {
        const parts = [];
        
        if (data.name) {
          parts.push(`I'm ${data.name}`);
        }
        
        if (data.title && data.company) {
          parts.push(`the ${data.title} at ${data.company}`);
        } else if (data.title) {
          parts.push(`a ${data.title}`);
        } else if (data.company) {
          parts.push(`from ${data.company}`);
        }
        
        if (data.industry_guess) {
          parts.push(`working in the ${data.industry_guess} industry`);
        }
        
        if (parts.length === 0) {
          return '';
        }
        
        // Join parts with commas and add a period
        let bio = parts.join(', ') + '.';
        
        // Add a second sentence about their business/role if we have company info
        if (data.company && data.industry_guess) {
          bio += ` I'm passionate about ${data.industry_guess} and excited to connect with like-minded professionals.`;
        } else if (data.company) {
          bio += ` I'm excited to connect and explore potential collaborations.`;
        }
        
        return bio;
      };
      
      // Map extracted data to profile fields
      const profileData = {
        name: analysis.name || '',
        position: analysis.title || '',
        company: analysis.company || '',
        email: analysis.email || user?.email || '',
        phone: analysis.phone || '',
        website: analysis.website || '',
        bio: generateFirstPersonBio(analysis),
      };
      
      setExtractedData(analysis);
      setProfile(prev => ({ ...prev, ...profileData }));
      setStep('review');
    } catch (err) {
      console.error('Processing error:', err);
      setError(err.message || 'Failed to process business card. You can enter your information manually.');
      setStep('manual');
    } finally {
      setIsProcessing(false);
      // Clear captured image after processing is done
      setCapturedImage(null);
    }
  };

  const handleInputChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!user?.uid) {
      setError('User not authenticated. Please sign in again.');
      return;
    }

    if (!profile.name || !profile.email) {
      setError('Please fill in required fields (Name and Email).');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');
      await saveUserProfile(user.uid, profile);
      
      // Small delay to ensure Firestore write is complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force page reload to trigger profile check and navigation
      window.location.href = '/';
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Error saving profile: ' + (error.message || 'Unknown error'));
      setIsProcessing(false);
    }
  };

  const handleSkip = async () => {
    // Save minimal profile to skip onboarding
    if (user?.uid) {
      try {
        setIsProcessing(true);
        // Save with email and a default name (from email or "User")
        const defaultName = user.displayName || user.email?.split('@')[0] || 'User';
        await saveUserProfile(user.uid, { 
          email: user.email,
          name: defaultName,
        });
        
        // Small delay to ensure Firestore write is complete
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Force page reload to trigger profile check
        window.location.href = '/';
      } catch (error) {
        console.error('Error saving profile on skip:', error);
        setIsProcessing(false);
        // Continue anyway - navigate to home
        window.location.href = '/';
      }
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 transition-colors duration-300">
      {/* Theme Toggle Button - Fixed Position */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>
      <div className="max-w-md mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Logo size="lg" />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-zinc-900 to-zinc-600 dark:from-zinc-100 dark:to-zinc-400 bg-clip-text text-transparent">
              Welcome to FollowUP
            </h1>
          </div>
          <p className="text-zinc-600 dark:text-zinc-400">
            Let's set up your profile
          </p>
        </div>

        {step === 'upload' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-zinc-900 rounded-2xl p-6 text-center border border-zinc-200 dark:border-zinc-800">
              <div className="text-6xl mb-4">📇</div>
              <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
                Upload Your Business Card
              </h2>
              <p className="text-zinc-600 dark:text-zinc-400 text-sm mb-6">
                We'll automatically extract your information, or you can enter it manually
              </p>
              
              <CameraCapture
                onCapture={handleImageCapture}
                isProcessing={isProcessing}
                error={error}
                capturedImage={capturedImage}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep('manual')}
                className="flex-1 py-3 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg font-medium transition-colors"
              >
                ✏️ Enter Manually
              </button>
              <button
                onClick={handleSkip}
                className="px-4 py-3 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg font-medium transition-colors"
              >
                Skip
              </button>
            </div>
          </div>
        )}

        {step === 'manual' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-zinc-900 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-800">
              <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-4">
                Enter Your Information
              </h2>

              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600 focus:border-transparent"
                    placeholder="Your full name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
                    Position / Title
                  </label>
                  <input
                    type="text"
                    value={profile.position}
                    onChange={(e) => handleInputChange('position', e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600 focus:border-transparent"
                    placeholder="e.g., CEO, Director"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
                    Company
                  </label>
                  <input
                    type="text"
                    value={profile.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600 focus:border-transparent"
                    placeholder="Your company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600 focus:border-transparent"
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600 focus:border-transparent"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
                    Website
                  </label>
                  <input
                    type="url"
                    value={profile.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600 focus:border-transparent"
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setStep('upload')}
                  className="flex-1 py-3 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg font-medium transition-colors"
                >
                  ← Back
                </button>
                <button
                  onClick={handleSave}
                  disabled={isProcessing || !profile.name || !profile.email}
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Saving...' : 'Save & Continue'}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-zinc-900 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-800">
              <div className="text-center mb-6">
                <div className="text-4xl mb-2">✅</div>
                <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
                  Information Extracted!
                </h2>
                <p className="text-zinc-600 dark:text-zinc-400 text-sm">
                  Review and edit your information below
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
                    Position / Title
                  </label>
                  <input
                    type="text"
                    value={profile.position}
                    onChange={(e) => handleInputChange('position', e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
                    Company
                  </label>
                  <input
                    type="text"
                    value={profile.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
                    Website
                  </label>
                  <input
                    type="url"
                    value={profile.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
                    Bio / About
                  </label>
                  <textarea
                    value={profile.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-zinc-600 focus:border-transparent resize-y min-h-[100px]"
                    placeholder="Tell us about yourself..."
                    rows="3"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setStep('upload')}
                  className="flex-1 py-3 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg font-medium transition-colors"
                >
                  ← Try Again
                </button>
                <button
                  onClick={handleSave}
                  disabled={isProcessing || !profile.name || !profile.email}
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Saving...' : 'Save & Continue'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Onboarding;

